import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BillingResponse, UpdateBillingRequest } from '@xver/@types/bill';
import { Button, InputField } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import {
    ThaiAddressSelector,
    ThaiAddressValue,
    concatThaiAddress,
} from '@xver/components/Molecules';

interface BillingAddressSectionProps {
    billing: BillingResponse | undefined;
    onSubmit: (payload: UpdateBillingRequest) => Promise<unknown>;
    isSubmitting?: boolean;
}

interface FormErrors {
    company?: string;
    taxId?: string;
    address?: string;
    province?: string;
    district?: string;
    subDistrict?: string;
    postalCode?: string;
}

type FormTouched = Partial<Record<keyof FormErrors, boolean>>;

export default function BillingAddressSection({
    billing,
    onSubmit,
    isSubmitting,
}: BillingAddressSectionProps) {
    const { t } = useTranslation('billings');

    const [company, setCompany] = useState('');
    const [taxId, setTaxId] = useState('');
    const [street, setStreet] = useState('');
    const [address, setAddress] = useState<ThaiAddressValue>({
        province: '',
        district: '',
        subDistrict: '',
        postalCode: '',
    });
    const [touched, setTouched] = useState<FormTouched>({});
    const [submitAttempted, setSubmitAttempted] = useState(false);

    useEffect(() => {
        if (!billing) return;
        setCompany(billing.company ?? '');
        setTaxId(billing.taxid ?? '');
        setStreet(billing.address ?? '');
    }, [billing]);

    const validation = useMemo<FormErrors>(() => {
        const errors: FormErrors = {};
        const co = company.trim();
        const ti = taxId.trim();
        const ad = street.trim();
        if (!co) errors.company = t('errors.companyRequired');
        else if (co.length > 64) errors.company = t('errors.companyLength');
        if (!ti) errors.taxId = t('errors.taxIdRequired');
        else if (!/^\d{13}$/.test(ti)) errors.taxId = t('errors.taxIdLength');
        if (!ad) errors.address = t('errors.addressRequired');
        if (!address.province) errors.province = t('errors.provinceRequired');
        if (!address.district) errors.district = t('errors.districtRequired');
        if (!address.subDistrict) errors.subDistrict = t('errors.subDistrictRequired');
        if (!address.postalCode) errors.postalCode = t('errors.postalCodeRequired');
        else if (address.postalCode.length !== 5) errors.postalCode = t('errors.postalCodeLength');
        return errors;
    }, [company, taxId, street, address, t]);

    const visibleErrors = useMemo<FormErrors>(() => {
        const out: FormErrors = {};
        (Object.keys(validation) as Array<keyof FormErrors>).forEach((key) => {
            if (submitAttempted || touched[key]) out[key] = validation[key];
        });
        return out;
    }, [validation, touched, submitAttempted]);

    const handleSubmit = async () => {
        if (!submitAttempted) setSubmitAttempted(true);
        if (Object.keys(validation).length > 0) return;
        await onSubmit({
            company: company.trim(),
            tax_id: taxId.trim(),
            address: concatThaiAddress(street, address),
        });
    };

    return (
        <DialogBox
            label={t('address.title')}
            description={t('address.description')}
            classNames="p-4 gap-4"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                    size="md"
                    label={t('address.company')}
                    placeholder={t('address.companyPlaceholder')}
                    value={company}
                    onChange={(e) => {
                        setCompany(e.target.value);
                        if (!touched.company) setTouched((p) => ({ ...p, company: true }));
                    }}
                    error={visibleErrors.company}
                    maxLength={64}
                />
                <InputField
                    size="md"
                    label={t('address.taxId')}
                    placeholder={t('address.taxIdPlaceholder')}
                    value={taxId}
                    onChange={(e) => {
                        setTaxId(e.target.value.replace(/\D/g, '').slice(0, 13));
                        if (!touched.taxId) setTouched((p) => ({ ...p, taxId: true }));
                    }}
                    error={visibleErrors.taxId}
                    maxLength={13}
                />
            </div>
            <InputField
                size="md"
                label={t('address.address')}
                placeholder={t('address.addressPlaceholder')}
                value={street}
                onChange={(e) => {
                    setStreet(e.target.value);
                    if (!touched.address) setTouched((p) => ({ ...p, address: true }));
                }}
                error={visibleErrors.address}
            />
            <ThaiAddressSelector
                value={address}
                onChange={(next) => {
                    setAddress(next);
                    setTouched((p) => ({
                        ...p,
                        province: true,
                        district: true,
                        subDistrict: true,
                        postalCode: true,
                    }));
                }}
                errors={{
                    province: visibleErrors.province,
                    district: visibleErrors.district,
                    subDistrict: visibleErrors.subDistrict,
                    postalCode: visibleErrors.postalCode,
                }}
                labels={{
                    province: t('address.province'),
                    provincePlaceholder: t('address.provincePlaceholder'),
                    district: t('address.district'),
                    districtPlaceholder: t('address.districtPlaceholder'),
                    subDistrict: t('address.subDistrict'),
                    subDistrictPlaceholder: t('address.subDistrictPlaceholder'),
                    postalCode: t('address.postalCode'),
                }}
            />
            <div className="flex justify-end">
                <Button onClick={handleSubmit} isLoading={isSubmitting}>
                    {t('address.save')}
                </Button>
            </div>
        </DialogBox>
    );
}
